import { ReactNode } from "react";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { NavLink } from "@/utils/sidebar";
import styles from "./WorkspaceSidebar.module.scss";

export interface ProductDropdownItem {
  id: string | number;
  name: string;
  icon?: string;
  accentColor?: string;
  link?: string;
}

export interface WorkspaceSidebarProps {
  navItems: NavLink[];
  selectedItem?: string;
  onSelect: (url: string) => void;
  workspaceName: string;
  workspaceIconSrc?: string;
  workspaceAccentColor?: string;
  organizationName?: string;
  onOrganizationClick?: (event: React.MouseEvent<HTMLElement>) => void;
  showProductSwitcher?: boolean;
  groupIcon?: ReactNode;
  isProductDropdownOpen?: boolean;
  productItems?: ProductDropdownItem[];
  onProductSwitcherClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onProductSelect?: (item: ProductDropdownItem) => void;
  userName?: string;
  userRole?: string;
  onSettingsClick?: () => void;
  shimmerEffect?: boolean;
  disabledLink?: boolean;
}

const WorkspaceSidebar = ({
  navItems,
  selectedItem,
  onSelect,
  workspaceName,
  workspaceIconSrc,
  workspaceAccentColor = "#3D79F2",
  showProductSwitcher = false,
  groupIcon,
  isProductDropdownOpen = false,
  productItems = [],
  onProductSwitcherClick,
  onProductSelect,
  shimmerEffect = false,
  disabledLink = false,
}: WorkspaceSidebarProps) => {
  const isActive = (url?: string) => Boolean(url && selectedItem && selectedItem === url);

  return (
    <aside className={styles.sidebar}>
      <div className={styles.header}>
        <div className={styles.workspaceHeader}>
          <div
            className={styles.workspaceIcon}
            style={{ backgroundColor: workspaceAccentColor }}
          >
            {workspaceIconSrc ? (
              <img src={workspaceIconSrc} alt={workspaceName} />
            ) : null}
          </div>
          <div className={styles.workspaceMeta}>
            <div className={styles.workspaceLabel}>WORKSPACE</div>
            <div className={styles.workspaceName}>{workspaceName}</div>
          </div>
          {showProductSwitcher ? (
            <button
              type="button"
              className={styles.switcherButton}
              onClick={onProductSwitcherClick}
              aria-label="Switch workspace product"
              aria-expanded={isProductDropdownOpen}
            >
              {groupIcon ?? <UnfoldMoreIcon className={styles.switcherIcon} />}
            </button>
          ) : null}
        </div>

        {isProductDropdownOpen && productItems.length > 0 ? (
          <div className={styles.productDropdown}>
            {productItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.productItem}
                onClick={() => onProductSelect?.(item)}
              >
                {item.icon ? (
                  <div
                    className={styles.productIcon}
                    style={{ backgroundColor: item.accentColor ?? workspaceAccentColor }}
                  >
                    <img src={item.icon} alt={item.name} />
                  </div>
                ) : null}
                <span>{item.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <nav className={styles.nav}>
        {shimmerEffect
          ? Array.from({ length: 8 }, (_, i) => i + 1).map((item) => (
              <div key={item} className={styles.shimmerItem}>
                <span className={styles.shimmerIcon} />
                <span className={styles.shimmerLabel} />
              </div>
            ))
          : navItems.map((item) => (
              <button
                key={`${item.id}-${item.url ?? item.label}`}
                type="button"
                className={`${styles.navItem} ${isActive(item.url) ? styles.navItemActive : ""}`}
                onClick={() => !disabledLink && item.url && onSelect(item.url)}
                disabled={disabledLink}
              >
                {item.icon ? <span className={styles.navIcon}>{item.icon}</span> : null}
                <span>{item.label}</span>
              </button>
            ))}
      </nav>
    </aside>
  );
};

export default WorkspaceSidebar;
